import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["size", "customWidth", "side", "sx", "onClose", "anchorEl", "componentsProps"];
import React from 'react';
import { Popper, extendSX, useTheme } from '@lumahealthhq/ui-core';

/**
 * Props interface for the Panel component
 * @interface PanelProps
 * @extends {PopperProps}
 */
import { jsx as _jsx } from "react/jsx-runtime";
/** Mapping of size values to percentage widths */
var PANEL_SIZE = {
  small: '40%',
  medium: '70%',
  large: '90%'
};

/**
 * A sliding panel component that can be anchored to an element and positioned on either side of the screen.
 *
 * @component
 * @example
 * ```tsx
 * <Panel
 *   size="medium"
 *   side="right"
 *   anchorEl={someElement}
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 * >
 *   Panel content
 * </Panel>
 * ```
 *
 * @param {PanelProps} props - Component props
 * @param {('small'|'medium'|'large')} [props.size='small'] - Controls panel width as percentage of parent
 * @param {string} [props.customWidth] - Optional custom width that overrides size prop
 * @param {('left'|'right')} [props.side='right'] - Which side the panel appears on
 * @param {HTMLElement|null} [props.anchorEl] - Element to anchor the panel to
 * @param {() => void} props.onClose - Callback when panel is closed (e.g. via Escape key)
 * @returns {React.ReactElement} The Panel component
 */
var Panel = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _componentsProps$root;
  var _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'small' : _ref$size,
    customWidth = _ref.customWidth,
    _ref$side = _ref.side,
    side = _ref$side === void 0 ? 'right' : _ref$side,
    sx = _ref.sx,
    onClose = _ref.onClose,
    anchorEl = _ref.anchorEl,
    componentsProps = _ref.componentsProps,
    props = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var isLeft = side === 'left';
  var eventListenerClosePopper = React.useCallback(function (e) {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);
  React.useEffect(function () {
    if (props.open) {
      window.addEventListener('keydown', eventListenerClosePopper);
    }
    return function () {
      window.removeEventListener('keydown', eventListenerClosePopper);
    };
  }, [eventListenerClosePopper, props.open]);
  var topMargin = React.useRef(anchorEl == null ? void 0 : anchorEl.getBoundingClientRect().top);
  var slideAnimation = isLeft ? {
    '@keyframes slideInFromLeft': {
      '0%': {
        transform: 'translateX(-100%)'
      },
      '100%': {
        transform: 'translateX(0)'
      }
    },
    '@keyframes slideOutToLeft': {
      '0%': {
        transform: 'translateX(0)'
      },
      '100%': {
        transform: 'translateX(-100%)'
      }
    },
    animation: props.open ? 'slideInFromLeft 0.3s ease-out' : 'slideOutToLeft 0.3s ease-out'
  } : {
    '@keyframes slideInFromRight': {
      '0%': {
        transform: 'translateX(100%)'
      },
      '100%': {
        transform: 'translateX(0)'
      }
    },
    '@keyframes slideOutToRight': {
      '0%': {
        transform: 'translateX(0)'
      },
      '100%': {
        transform: 'translateX(100%)'
      }
    },
    animation: props.open ? 'slideInFromRight 0.3s ease-out' : 'slideOutToRight 0.3s ease-out'
  };
  return /*#__PURE__*/_jsx(Popper, _extends({
    ref: ref,
    componentsProps: _extends({}, componentsProps, {
      root: _extends({}, componentsProps == null ? void 0 : componentsProps.root, {
        style: _extends({
          top: undefined,
          bottom: '0px',
          left: isLeft ? '0px' : undefined,
          right: isLeft ? undefined : '0px',
          width: customWidth != null ? customWidth : PANEL_SIZE[size],
          height: "calc(100% - ".concat(topMargin.current || 0, "px)")
        }, componentsProps == null ? void 0 : (_componentsProps$root = componentsProps.root) == null ? void 0 : _componentsProps$root.style)
      })
    }),
    sx: extendSX(sx, {
      '& > :first-child': _extends({
        height: '100%',
        overflowY: 'auto',
        p: theme.spacing(3),
        position: 'relative',
        borderRadius: theme.spacing(2),
        boxShadow: 'none',
        borderLeft: isLeft ? undefined : "2px solid ".concat(theme.palette.primary[200]),
        borderRight: isLeft ? "2px solid ".concat(theme.palette.primary[200]) : undefined
      }, slideAnimation),
      paddingBottom: theme.spacing(2)
    })
  }, props));
});
Panel.displayName = 'Panel';
export default Panel;